import React from "react";
import { CheckCircle, AlertTriangle, XCircle } from "react-feather";
import GuardAvatar from "./GuardAvatar";

const STATUS_META = {
  RECOMMENDED: { label: "Recommended", cls: "status-recommended", icon: CheckCircle },
  POSSIBLE_REVIEW: { label: "Review", cls: "status-review", icon: AlertTriangle },
  NOT_ELIGIBLE: { label: "Not Eligible", cls: "status-not-eligible", icon: XCircle },
};

const AREA_LABEL = { SAME: "Same Area", DIFFERENT: "Different Area", UNKNOWN: "Area Unknown" };

const scoreCls = (score) => (score >= 70 ? "score-high" : score >= 40 ? "score-mid" : "score-low");

// Card-row list, not a raw table - this app's own established "card-rows for on-screen
// reports" convention (see MiniSummaryTable/mini-summary-row), extended here into a full
// recommendation card per guard: avatar+identity, a compact facts grid, a prominent score
// ring, reason chips, a status pill, and the Select action - reads as a ranked recommendation,
// not a spreadsheet line. Area replaces the mockup's km "Distance" in the same visual role,
// since no lat/long data exists anywhere in this system.
const RecommendedGuardsTable = ({ candidates, selectedGuids, onToggleSelect, canEdit, atSelectionCap }) => {
  if (candidates.length === 0) {
    return <div className="text-muted small py-3">No candidates match the current filters.</div>;
  }

  return (
    <div>
      {candidates.map((c, index) => {
        const meta = STATUS_META[c.status] || STATUS_META.NOT_ELIGIBLE;
        const StatusIcon = meta.icon;
        const isSelected = selectedGuids.has(c.employeeRecordGuid);
        const isSelectable = canEdit && c.status !== "NOT_ELIGIBLE" && !(atSelectionCap && !isSelected);

        return (
          <div key={c.employeeRecordGuid} className={`guard-candidate-card ${meta.cls} ${isSelected ? "is-selected" : ""}`}>
            <div className="guard-candidate-rank">{index + 1}</div>

            <GuardAvatar employeeRecordGuid={c.employeeRecordGuid} hasPhoto={c.hasPhoto} />

            <div className="guard-candidate-identity">
              <div className="fw-semibold text-truncate">{c.empName} {c.empLastName}</div>
              <div className="text-muted small">G-{c.empCode}</div>
            </div>

            <div className="guard-candidate-facts">
              <div>
                <div className="guard-candidate-fact-label">Current Assignment</div>
                <div className="guard-candidate-fact-value">
                  {c.currentAssignmentCustName ? (
                    <>
                      {c.currentAssignmentCustName}
                      {c.currentAssignmentEndDate && ` · ends ${new Date(c.currentAssignmentEndDate).toLocaleDateString()}`}
                    </>
                  ) : (
                    "Not assigned"
                  )}
                </div>
              </div>
              <div>
                <div className="guard-candidate-fact-label">Area</div>
                <div className="guard-candidate-fact-value">{AREA_LABEL[c.areaMatch] || "Area Unknown"}</div>
              </div>
              <div>
                <div className="guard-candidate-fact-label">Grade</div>
                <div className="guard-candidate-fact-value">
                  {c.grade ? c.grade.charAt(0) + c.grade.slice(1).toLowerCase() : "-"}
                </div>
              </div>
            </div>

            <div className="guard-candidate-reasons">
              {c.keyReasons.slice(0, 2).map((r, i) => (
                <span key={i} className="guard-reason-chip" title={r}>{r}</span>
              ))}
            </div>

            <div className={`guard-score-ring ${scoreCls(c.matchScore)}`}>
              <span className="ring-value">{c.matchScore}%</span>
              <span className="ring-label">Match</span>
            </div>

            <span className={`guard-candidate-status-pill ${meta.cls}`}>
              <StatusIcon size={12} /> {meta.label}
            </span>

            {canEdit && (
              <button
                type="button"
                className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-outline-primary"}`}
                disabled={!isSelectable}
                onClick={() => onToggleSelect(c)}
              >
                {isSelected ? "Selected" : "Select"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RecommendedGuardsTable;
